import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/axios'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const toast = useToast()
  
  // State
  const user = ref(null)
  const token = ref(localStorage.getItem('token') || null)
  const refreshToken = ref(localStorage.getItem('refreshToken') || null)
  const loading = ref(false)
  const error = ref(null)
  
  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const currentUser = computed(() => user.value)
  
  // Token is now handled automatically by the API interceptor
  
  // Actions
  async function login(credentials) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/auth/login', credentials)
      const { user: userData, access_token, refresh_token } = response.data
      
      // Store tokens
      token.value = access_token
      refreshToken.value = refresh_token
      user.value = userData
      
      // Save to localStorage
      localStorage.setItem('token', access_token)
      localStorage.setItem('refreshToken', refresh_token)
      localStorage.setItem('user', JSON.stringify(userData))
      
      // Token will be automatically included by API interceptor
      
      toast.success('Login successful!')
      router.push('/dashboard')
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Login failed'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function register(userData) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/auth/register', userData)
      const { user: newUser, access_token, refresh_token } = response.data
      
      // Store tokens
      token.value = access_token
      refreshToken.value = refresh_token
      user.value = newUser
      
      // Save to localStorage
      localStorage.setItem('token', access_token)
      localStorage.setItem('refreshToken', refresh_token)
      localStorage.setItem('user', JSON.stringify(newUser))
      
      // Token will be automatically included by API interceptor
      
      toast.success('Registration successful!')
      router.push('/dashboard')
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Registration failed'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function logout() {
    try {
      await api.post('/auth/logout')
    } catch (err) {
      // Continue with logout even if API call fails
    }
    
    clearTokens()
    toast.info('Logged out successfully')
    router.push('/login')
  }
  
  async function refreshAccessToken() {
    try {
      const response = await api.post('/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken.value}`
        }
      })
      
      const { access_token } = response.data
      
      // Update token
      token.value = access_token
      localStorage.setItem('token', access_token)
      setAxiosToken(access_token)
      
      return access_token
    } catch (err) {
      // Refresh failed, logout user
      await logout()
      throw err
    }
  }
  
  async function checkAuth() {
    if (!token.value || !refreshToken.value) {
      // No tokens, clear everything and return false
      clearTokens()
      return false
    }
    
    try {
      const response = await api.get('/auth/profile')
      user.value = response.data.user
      localStorage.setItem('user', JSON.stringify(response.data.user))
      return true
    } catch (err) {
      if (err.response?.status === 401 && refreshToken.value) {
        // Try to refresh token once
        try {
          await refreshAccessToken()
          // Retry profile fetch
          const response = await api.get('/auth/profile')
          user.value = response.data.user
          localStorage.setItem('user', JSON.stringify(response.data.user))
          return true
        } catch (refreshErr) {
          // Refresh failed, clear tokens
          clearTokens()
          return false
        }
      }
      // Other error or no refresh token
      clearTokens()
      return false
    }
  }
  
  function clearTokens() {
    token.value = null
    refreshToken.value = null
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    // Token removal handled by API interceptor
  }
  
  async function updateProfile(profileData) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.put('/auth/profile', profileData)
      user.value = response.data.user
      localStorage.setItem('user', JSON.stringify(response.data.user))
      toast.success('Profile updated successfully!')
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Update failed'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  // Initialize user from localStorage
  if (localStorage.getItem('user')) {
    try {
      user.value = JSON.parse(localStorage.getItem('user'))
    } catch (err) {
      localStorage.removeItem('user')
    }
  }
  
  // Note: Token refresh is now handled in the centralized axios instance
  
  return {
    // State
    user,
    token,
    loading,
    error,
    
    // Getters
    isAuthenticated,
    currentUser,
    
    // Actions
    login,
    register,
    logout,
    refreshAccessToken,
    checkAuth,
    updateProfile
  }
})